import { useState, useEffect } from "react";
const isPack = (k) => /^[a-z]+_(policy_pack|full_policy_package)$/.test(k || "");
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { logError } from "@/lib/log";
import { api, startCheckout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WebOnlyPurchase } from "@/components/WebOnlyPurchase";
import { PayByInvoiceLink } from "@/components/PayByInvoice";
import { Price } from "@/components/Price";
import PackPreviewDialog from "@/components/PackPreviewDialog";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText, GraduationCap, Package, Loader2, Check, ShoppingCart, Eye, Download,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["All", "CQC", "Ofsted", "Clinical Aesthetics", "GP & Hospitals", "Fostering", "Supported Living", "Mock Interview", "Start-up"];

const TYPE_ICON = { policy: FileText, mock_interview: GraduationCap, resource: Package };

export default function Store() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [filter, setFilter] = useState("All");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [packPreview, setPackPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/resources").then(({ data }) => setResources(data)).finally(() => setLoading(false));
    api.get("/sponsors/featured").then(({ data }) => setFeatured(data)).catch((err) => logError("Failed to load sponsors", err));
  }, []);

  const owned = new Set(user?.purchased_lookup_keys || []);

  const buy = async (res) => {
    if (!user) { navigate("/login"); return; }
    if (!res.lookup_key) { toast.error("This item is not purchasable yet"); return; }
    setBusy(res.id);
    try {
      await startCheckout(res.lookup_key);
    } catch {
      toast.error("Could not start checkout");
      setBusy(null);
    }
  };

  const filtered = filter === "All" ? resources : resources.filter((r) => r.category === filter);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="max-w-2xl mb-8">
        <span className="overline text-accent">Resource Store</span>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-2 mb-3">
          Policies, procedures &amp; mock interviews
        </h1>
        <p className="text-muted-foreground text-lg">
          Inspection-ready resources you can buy individually — mapped to CQC and Ofsted standards. Prices are as shown — no VAT is added.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
              filter === c ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}>
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((res) => {
            const Icon = TYPE_ICON[res.type] || Package;
            const isOwned = owned.has(res.lookup_key);
            return (
              <div key={res.id} data-testid={`resource-card-${res.id}`}
                className="bg-card border border-border rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <Badge variant="secondary">{res.category}</Badge>
                </div>
                <h3 className="font-display font-semibold text-lg leading-snug mb-2">{res.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">{res.description}</p>
                  <div className="flex items-center justify-between mb-4">
                  <Price value={res.price_gbp} compact className="font-display text-2xl font-extrabold" testId={`resource-price-${res.id}`} />
                  {user?.founder?.pack_discount_available && isPack(res.lookup_key) && !isOwned && <span className="ml-2 text-[11px] font-bold rounded-full bg-amber-100 text-amber-900 px-2 py-0.5" data-testid={`resource-founder-tag-${res.id}`}>Founder: 50% off at checkout</span>}
                  {res.pages > 0 ? <span className="text-xs text-muted-foreground">{res.pages} pages</span>
                    : res.type === "mock_interview" && <span className="text-xs text-muted-foreground">AI-led · scored report</span>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setPreview(res)}
                    data-testid={`preview-resource-${res.id}`}>
                    <Eye className="w-4 h-4 mr-1.5" /> Preview
                  </Button>
                  {isOwned ? (
                    <Button asChild size="sm" className="flex-1" variant="secondary" data-testid={`owned-resource-${res.id}`}>
                      <Link to={res.type === "mock_interview" ? "/mock-interview" : "/dashboard"}>
                        {res.type === "mock_interview" ? <><GraduationCap className="w-4 h-4 mr-1.5" /> Start</> : <><Download className="w-4 h-4 mr-1.5" /> Download</>}
                      </Link>
                    </Button>
                  ) : (
                    <WebOnlyPurchase compact>
                    <Button size="sm" className="flex-1" onClick={() => buy(res)} disabled={busy === res.id}
                      data-testid={`buy-resource-button-${res.id}`}>
                      {busy === res.id ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <><ShoppingCart className="w-4 h-4 mr-1.5" /> Buy</>}
                    </Button>
                    <PayByInvoiceLink lookupKey={res.lookup_key} title={res.title} amount={res.price_gbp} className="basis-full justify-center mt-1" />
                    </WebOnlyPurchase>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="resource-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <Badge variant="secondary" className="w-fit mb-1">{preview.category}</Badge>
                <DialogTitle className="font-display text-xl">{preview.title}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground leading-relaxed">{preview.description}</p>
              {preview.toc?.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-semibold mb-2">What's included</p>
                  <ul className="space-y-1.5">
                    {preview.toc.map((t) => (
                      <li key={t} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="w-4 h-4 text-accent mt-0.5 shrink-0" /> {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t border-border gap-3 flex-wrap">
                <Price value={preview.price_gbp} className="font-display text-2xl font-extrabold" testId="preview-price" />
                <div className="flex gap-2">
                  {preview.type === "policy" && (
                    <Button variant="outline" onClick={() => setPackPreview(preview)} data-testid="preview-policy-list-button"><Eye className="w-4 h-4 mr-1.5" /> Policy list &amp; sample</Button>
                  )}
                  {preview.pages > 0 && !owned.has(preview.lookup_key) && preview.type !== "policy" && (
                    <Button asChild variant="outline" data-testid="preview-pages-button">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/resources/${preview.id}/preview`} target="_blank" rel="noreferrer"><Eye className="w-4 h-4 mr-1.5" /> Preview pages</a>
                    </Button>
                  )}
                {owned.has(preview.lookup_key) ? (
                  <Button asChild variant="secondary"><Link to={preview.type === "mock_interview" ? "/mock-interview" : preview.type === "policy" ? "/policies" : "/dashboard"}><Download className="w-4 h-4 mr-1.5" /> Owned — open</Link></Button>
                ) : (
                  <WebOnlyPurchase>
                  <Button onClick={() => buy(preview)} disabled={busy === preview.id} data-testid="preview-buy-button">
                    <ShoppingCart className="w-4 h-4 mr-1.5" /> {user ? `Buy for £${preview.price_gbp}` : "Sign in to buy"}
                  </Button>
                  <PayByInvoiceLink lookupKey={preview.lookup_key} title={preview.title} amount={preview.price_gbp} className="ml-3" />
                  </WebOnlyPurchase>
                )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <PackPreviewDialog resource={packPreview} onClose={() => setPackPreview(null)} />
      {featured.length > 0 && (
        <section className="mt-14" data-testid="featured-partners">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700 dark:text-amber-300">Featured partners</span>
            <span className="text-xs text-muted-foreground">Sponsored · vetted suppliers to UK care providers</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((sp) => (
              <a key={sp.id} href={sp.url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => api.post(`/sponsors/${sp.id}/click`).catch(() => {})}
                className="flex gap-3 items-start bg-card border border-border rounded-2xl p-4 hover:border-amber-400/60 transition-colors" data-testid={`featured-partner-${sp.id}`}>
                {sp.logo_url ? <img src={sp.logo_url} alt={sp.company} className="w-12 h-12 rounded-lg object-contain bg-white border border-border shrink-0" />
                  : <div className="w-12 h-12 rounded-lg bg-amber-500/15 text-amber-700 flex items-center justify-center font-display font-bold shrink-0">{sp.company.slice(0, 2).toUpperCase()}</div>}
                <div className="min-w-0"><p className="font-semibold text-sm truncate">{sp.company}</p><p className="text-xs text-muted-foreground line-clamp-2">{sp.description}</p></div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
